import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Typography, theme } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

const { Title, Text, Link } = Typography;

// ── Mini document "0" illustration ───────────────────────────────────────────

const DocZero: React.FC<{ borderColor: string; lineColor: string; accentColor: string }> = ({
    borderColor,
    lineColor,
    accentColor,
}) => {
    const { token } = theme.useToken();
    return (
    <Flex
        vertical
        gap={5}
        style={{
            display: "inline-flex",
            padding: "10px 14px",
            background: "transparent",
            border: `1px solid ${borderColor}`,
            borderRadius: 10,
            marginInline: 6,
            position: "relative",
            verticalAlign: "middle",
            top: -8,
        }}
    >
        {/* 404 badge */}
        <span
            style={{
                position: "absolute",
                top: -11,
                right: -11,
                fontSize: 10,
                fontWeight: 700,
                color: token.colorError,
                background: token.colorErrorBg,
                border: `1px solid ${token.colorErrorBorder}`,
                borderRadius: 4,
                padding: "1px 6px",
                lineHeight: 1.6,
            }}
        >
            404
        </span>

        {/* Document lines */}
        {[
            { width: 52, color: lineColor },
            { width: 36, color: lineColor },
            { width: 44, color: accentColor, opacity: 0.5 },
            { width: 52, color: lineColor },
            { width: 36, color: lineColor },
        ].map((line, i) => (
            <div
                key={i}
                style={{
                    width: line.width,
                    height: 6,
                    borderRadius: 3,
                    background: line.color,
                    opacity: line.opacity ?? 1,
                }}
            />
        ))}
    </Flex>
  );
};

// ── Quick links ───────────────────────────────────────────────────────────────

const QUICK_LINKS = [
    { label: "Facturas", path: "/facturas" },
    { label: "Contratos", path: "/contratos" },
    { label: "Contabilidad", path: "/contabilidad" },
];

// ── Main page ─────────────────────────────────────────────────────────────────

const NotFoundPage: React.FC<{ description?: string }> = ({ description = "" }) => {
    const navigate = useNavigate();
    const { token } = theme.useToken();

    return (
        <Flex
            vertical
            align="center"
            justify="center"
            style={{
                minHeight: "100vh",
                padding: "40px 24px",
                background: token.colorBgLayout,
                textAlign: "center",
            }}
        >
            {/* ── Big 404 with doc in the middle ── */}
            <div
                style={{
                    fontSize: 96,
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: -4,
                    color: token.colorText,
                    margin: 0,
                    userSelect: "none",
                }}
            >
                4
                <DocZero
                    borderColor={token.colorBorderSecondary}
                    lineColor={token.colorFillSecondary}
                    accentColor={token.colorPrimary}
                />
                4
            </div>

            {/* ── Copy ── */}
            <Title
                level={3}
                style={{
                    margin: "28px 0 8px",
                    fontWeight: 800,
                    fontSize: 22,
                    letterSpacing: "-0.02em",
                }}
            >
                Página no encontrada
            </Title>

            <Text
                type="secondary"
                style={{
                    fontSize: 15,
                    lineHeight: 1.7,
                    maxWidth: 320,
                    display: "block",
                    marginBottom: 32,
                }}
            >
                {description}
            </Text>

            {/* ── CTA ── */}
            <Button
                type="primary"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/dashboard")}
                style={{
                    borderRadius: token.borderRadiusLG,
                    fontWeight: 700,
                    height: 46,
                    paddingInline: 28,
                }}
            >
                Volver al inicio
            </Button>

            {/* ── Quick links ── */}
            <Flex gap={20} style={{ marginTop: 20 }}>
                {QUICK_LINKS.map((item) => (
                    <Link
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        style={{
                            fontSize: 13,
                            color: token.colorTextSecondary,
                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                            paddingBottom: 1,
                            cursor: "pointer",
                        }}
                    >
                        {item.label}
                    </Link>
                ))}
            </Flex>
        </Flex>
    );
};

export default NotFoundPage;